// @flow
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../../components/Input/Input';
import Radio from '../../../../../../components/Radio/Radio';
import { useAppSelector } from '../../../../../../reduxService/hooks';
import { EConversionSIToImperial, conversionLabel, convertTo } from '../../../../../conversion';
import { MaterialBioType } from '../../../../../enum/Material';
import { roundToFour, roundToTwo } from '../../Geometry/utils';
import MaterialDataSliders from '../MaterialDataSliders/MaterialDataSliders';
import { IFormikForm } from '../ModalMaterial/FormikMaterial';
import { get } from 'lodash';
import { selectProjectInfo } from '../../../../../store/projectInfo/projectInfoSlice';
import { IPhysicalDataResponse } from '../../../../../api/IApiResponse';
import { CheckForAllMats } from '../MaterialDataCheckboxes/CheckForAllMats';
import { UnitOfMeasure } from '../../../../../enum/project';

type Props = {};

export const MacMat = (props: Props) => {
    const projectInfo = useAppSelector(selectProjectInfo);
    const { t } = useTranslation();
    let isImperial = projectInfo?.data?.unitOfMeasure == UnitOfMeasure.Imperial;

    const { values, setFieldValue } = useFormikContext<IFormikForm>();
    const [isVegetated, setIsVegetated] = useState<boolean | undefined>(values[values.sectorChannel].isVegetated);
    let physicalData: IPhysicalDataResponse[] = get(values, `${values.sectorChannel}.material.physicalData`, []);

    const handleBioTypeChange = (bioType: MaterialBioType) => {
        setIsVegetated(bioType === MaterialBioType.Vegetated);
        setFieldValue(`${values.sectorChannel}.isVegetated`, bioType === MaterialBioType.Vegetated);
        if (bioType === MaterialBioType.Vegetated) {
            setFieldValue(`${values.sectorChannel}.vegetatedAllowShearStress`, physicalData![0].vegetatedAllowShearStress!);
            setFieldValue(`${values.sectorChannel}.vegetatedRoughness`, physicalData![0].vegetatedRoughness!);
        } else {
            setFieldValue(`${values.sectorChannel}.allowShearStress`, physicalData![0].allowShearStress!);
            setFieldValue(`${values.sectorChannel}.roughness`, physicalData![0].roughness!);
        }
    };

    const handleSlidersChange = (data: number, rf: number, time: number) => {
        setFieldValue(`${values.sectorChannel}.isVegetated`, isVegetated);
        if (isVegetated) {
            setFieldValue(`${values.sectorChannel}.vegetatedAllowShearStress`, data);
        } else {
            setFieldValue(`${values.sectorChannel}.allowShearStress`, data);
        }
        setFieldValue(`${values.sectorChannel}.rf`, rf);
        setFieldValue(`${values.sectorChannel}.time`, time);
    };

    const handleRoughnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = parseFloat(e.target.value);
        if (isVegetated) {
            setFieldValue(`${values.sectorChannel}.vegetatedRoughness`, roundToFour(value));
        } else {
            setFieldValue(`${values.sectorChannel}.roughness`, roundToFour(value));
        }
    };

    let defaultValueRf, defaultValueTime;
    if (!!physicalData && physicalData?.length > 0) {
        defaultValueRf = !!values[values.sectorChannel]?.rf ? values[values.sectorChannel].rf : physicalData![0].defaultRf;
        defaultValueTime = !!values[values.sectorChannel]?.time ? values[values.sectorChannel].time : physicalData![0].defaultTime;
    }

    let stress = isVegetated ? roundToTwo(values[values.sectorChannel].vegetatedAllowShearStress ?? 0) : roundToTwo(values[values.sectorChannel].allowShearStress ?? 0);
    if (isImperial) stress = roundToTwo(convertTo(stress, EConversionSIToImperial.Nm2_to_lbfft2));

    return (
        <>
            <div className='d-flex flex-column align-items-start'>
                <CheckForAllMats />
                <Radio
                    title={t('material_vegetated')}
                    name='isVegetated'
                    value={MaterialBioType.Vegetated}
                    defaultChecked={isVegetated}
                    onChange={e => handleBioTypeChange(parseInt(e.target.value))}
                />
                <Radio
                    title={t('material_unvegetated')}
                    defaultChecked={!isVegetated}
                    name='isVegetated'
                    value={MaterialBioType.UnvegetatedWithBioTextile}
                    onChange={e => handleBioTypeChange(parseInt(e.target.value))}
                />
            </div>
            {physicalData && physicalData.length > 0 && (
                <MaterialDataSliders
                    isVegetated={isVegetated}
                    defaultValueRf={defaultValueRf || physicalData![0].maxRf!}
                    maxRf={physicalData![0].maxRf!}
                    minRf={physicalData![0].minRf!}
                    stepRf={0.1}
                    defaultValueTime={defaultValueTime || physicalData![0].maxTime!}
                    maxTime={physicalData![0].maxTime!}
                    minTime={physicalData![0].minTime!}
                    stepTime={1}
                    onChange={handleSlidersChange}
                />
            )}
            <div className='d-flex justify-content-between mt-2 gap-2'>
                <Input
                    title={t('material_shear_stress', { unit_measure: conversionLabel.Nm2_lbfft2[projectInfo.data?.unitOfMeasure as number] })}
                    type='number'
                    name={isVegetated ? `${values.sectorChannel}.vegetatedAllowShearStress` : `${values.sectorChannel}.allowShearStress`}
                    value={stress}
                    disabled
                />
                <Input
                    title={t('material_roughness')}
                    type='number'
                    name={isVegetated ? `${values.sectorChannel}.vegetatedRoughness` : `${values.sectorChannel}.roughness`}
                    value={isVegetated ? roundToFour(values[values.sectorChannel].vegetatedRoughness ?? 0) : roundToFour(values[values.sectorChannel].roughness ?? 0)}
                    onChange={e => handleRoughnessChange(e)}
                />
            </div>
        </>
    );
};
